import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // ── INSTANT SLIDE SWAP (same technique as product-card.js) ────────────
    // Use select({ id: mediaId }) which matches against slide-id attributes.
    // This is synchronous and requires zero network activity — identical to
    // how SwatchesVariantPickerComponent works on product cards.
    const featuredMediaId = event.detail?.resource?.featured_media?.id;

    if (featuredMediaId != null && this.refs.slideshow) {
      this.refs.slideshow.select(
        { id: String(featuredMediaId) },undefined,
        { animate: false }
      );
    }
    // ── END INSTANT SWAP ──────────────────────────────────────────────────

    // Server replacement: only run if the media set has actually changed
    // (e.g. hide_variants mode). Skip it when media is identical to avoid
    // the DOM replacement flash.
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const currentMediaIds = Array.from(this.querySelectorAll('[data-media-id]'))
      .map((el) => el.dataset.mediaId)
      .join(',');

    const incomingMediaIds = Array.from(newMediaGallery.querySelectorAll('[data-media-id]'))
      .map((el) => el.dataset.mediaId)
      .join(',');

    if (currentMediaIds === incomingMediaIds) return;

    this.refs.slideshow?.select(0, undefined, { animate: false });
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
